import type { JSX } from 'react';
import { Localized } from 'dg-web-shared/lib/Localization.tsx';
import { Box, InputLabel, Stack } from '@mui/material';
import {
    DoNotDisturbOn,
    PanTool,
    PlayCircle,
    StopCircle,
} from '@mui/icons-material';
import * as Date from 'dg-web-shared/lib/Date.ts';
import { Formatter } from 'dg-web-shared/lib/Date.ts';
import {
    ApprovingContractInfo,
    ApprovingContractType,
    BarrierGateIdentificationEntity,
    OffstreetTransactionActions,
    PassageInfo,
} from './OperatorOffstreetTransactionTypes.ts';
import { TerminalProvider } from '../common/state/useParkingsFetch.ts';
import {
    IdentificationIcon,
    IdentificationType,
} from '../common/components/IdentificationItems.tsx';
import { Colors } from 'dg-web-shared/ui/vars.ts';
import { RoutedModalController } from 'dg-web-shared/common/components/material-ui/routed-modal/RoutedModal.tsx';
import {
    ModalVariant,
    OperatorRoutedModal,
} from '../ui/modal/OperatorRoutedModal.tsx';
import { ReadOnlyTextField } from '../ui/material-ui/ReadOnlyTextField.tsx';
import { PoolFullInfo } from './OperatorOffstreetTransactionEntryWithoutExit.tsx';
import { EmDash } from 'dg-web-shared/lib/Punctuation.ts';
import { addSpacesEveryFourthFromEnd } from './OperatorOffstreetTransactionsRoute.tsx';
import { Link } from 'react-router-dom';
import {
    envIsProduction,
    envIsTest,
    envName,
} from 'dg-web-shared/lib/Environment.ts';
import { Base64 } from 'dg-web-shared/common/utils/Base64.ts';
import { useQuery } from '../shared-mui-components/filter/OperatorFilterHelpers.tsx';
import { ParkingaboTestValidationLink } from '../parkingabo-users/ParkingaboUsersUtils.tsx';
import { TransactionRefundBlockedExplanation } from '../common/models/OperatorRefund.ts';
import { Ticket, TicketType } from 'dg-web-shared/model/Ticket.ts';
import { DateTime } from 'luxon';
import getDuration = Formatter.getDuration;

export function OperatorOffstreetTransactionDetailRoutedModal({
    render,
}: {
    render: (controller: RoutedModalController) => React.ReactNode;
}) {
    const query = useQuery();
    return (
        <OperatorRoutedModal
            variant={ModalVariant.STANDARD}
            render={render}
            backUrl={`..?${query}`}
        />
    );
}

export function OffstreetTransactionOpen({
    isPresent,
    hasAntipassback,
    displayLpn,
    isCancelled,
}: {
    isPresent: boolean;
    hasAntipassback: boolean;
    displayLpn?: string | null;
    isCancelled?: boolean;
}) {
    return (
        <ReadOnlyTextField
            label={
                <Localized
                    de="Anwesenheit"
                    fr="Présence"
                    it="Presenza"
                    en="Presence"
                />
            }
            value={
                <Box sx={{ display: 'flex', flexDirection: 'row', gap: '4px' }}>
                    <TransactionPresenceState
                        isPresent={isPresent}
                        hasAntipassback={hasAntipassback}
                        isCancelled={isCancelled}
                    />
                    {displayLpn && ` — ${displayLpn}`}
                </Box>
            }
        />
    );
}

function TransactionPresenceState({
    isPresent,
    hasAntipassback,
    isCancelled,
}: {
    isPresent: boolean;
    hasAntipassback: boolean;
    isCancelled?: boolean;
}) {
    if (isCancelled) {
        return (
            <>
                <DoNotDisturbOn color={'secondary'} />
                <Localized
                    de="Annulliert"
                    fr="Annulée"
                    it="Annullata"
                    en="Cancelled"
                />
            </>
        );
    }
    if (isPresent) {
        if (hasAntipassback) {
            return (
                <>
                    <PlayCircle color={'success'} />
                    <Localized
                        de="Im Parking ("
                        fr="Dans le parking ("
                        it="Nel parcheggio ("
                        en="In the parking ("
                    />
                    <PanTool
                        sx={{
                            color: Colors.yellow,
                            marginLeft: '-6px',
                        }}
                    />
                    <Localized
                        de="Antipassback aktiv)"
                        fr="anti-passback actif)"
                        it="anti-passback attivo)"
                        en="anti-passback active)"
                    />
                </>
            );
        }
        return (
            <>
                <PlayCircle color={'success'} />
                <Localized
                    de="Im Parking"
                    fr="Dans le parking"
                    it="Nel parcheggio"
                    en="In the parking"
                />
            </>
        );
    }
    return (
        <>
            <StopCircle color={'secondary'} />
            <Localized de="Ausgefahren" fr="Sorti" it="Uscito" en="Exited" />
        </>
    );
}

export function BarrierGateIdentificationEnityValue({
    barrierGateIdentificationEntity,
    barrierGateProvider,
}: {
    barrierGateIdentificationEntity: BarrierGateIdentificationEntity;
    barrierGateProvider?: TerminalProvider | null;
}) {
    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                }}
            >
                <BarrierGateIdentificationEnityIcon
                    barrierGateIdentificationEntity={
                        barrierGateIdentificationEntity
                    }
                />
                <BarrierGateIdentificationEnityText
                    barrierGateIdentificationEntity={
                        barrierGateIdentificationEntity
                    }
                    barrierGateProvider={barrierGateProvider}
                />
            </Box>
            <TestLink
                barrierGateIdentificationEntity={
                    barrierGateIdentificationEntity
                }
            />
        </>
    );
}

function TestLink({
    barrierGateIdentificationEntity,
}: {
    barrierGateIdentificationEntity: BarrierGateIdentificationEntity;
}) {
    switch (barrierGateIdentificationEntity.identificationMedia) {
        case IdentificationType.TICKET:
            return (
                <ParkingpayTestTicketLink
                    ticket={barrierGateIdentificationEntity.ticket}
                />
            );
        case IdentificationType.BADGE:
            return (
                <ParkingaboTestValidationLink
                    badgeLabelNr={barrierGateIdentificationEntity.labelNr}
                />
            );
        case IdentificationType.LICENSE_PLATE:
        case IdentificationType.QR_CODE:
            return null;
    }
}

function ParkingpayTestTicketLink({ ticket }: { ticket: Ticket }) {
    if (envIsProduction()) {
        return null;
    }
    const ticketUrl = getTicketUrl(ticket);
    return (
        <Box
            sx={{
                fontStyle: 'italic',
                marginTop: '12px',
            }}
        >
            <span>Nur nicht PROD:</span>
            <br />
            <a href={ticketUrl} target="_blank" rel="noreferrer">
                {ticketUrl}
            </a>
        </Box>
    );
}

function getTicketUrl(ticket: Ticket) {
    const hostname = `https://customer.${envName().toLowerCase()}.digitalparking.${
        envIsTest() ? 'ch' : 'dev'
    }`;

    switch (ticket.ticketType) {
        case TicketType.MC4_PUNCH_CARD:
        case TicketType.TRX_PUNCH_CARD: {
            const ticketBase64 = serializeTicktApiToBase64(
                ticket.ticketDisplay,
            );
            return `${hostname}/t/${ticketBase64}`;
        }
        case TicketType.TRX_QR_TICKET:
            return `${hostname}/qrt/${ticket.ticketId}`;
    }
}

function serializeTicktApiToBase64(ticketApiString: string) {
    function bigEndianBytes(i: number): Uint8Array {
        const bytes = new Uint8Array(2);
        bytes[1] = i & 0xff;
        bytes[0] = (i >> 8) & 0xff;
        return bytes;
    }

    const ticketParts = ticketApiString.split('.');
    const byteArray = [
        ...bigEndianBytes(Number(ticketParts[0])),
        ...bigEndianBytes(Number(ticketParts[3])),
        ...bigEndianBytes(Number(ticketParts[1])),
        ...bigEndianBytes(Number(ticketParts[2])),
    ];

    return Base64.encodeURLSafe(String.fromCharCode(...byteArray));
}

export function BarrierGateIdentificationEnityIcon({
    barrierGateIdentificationEntity,
}: {
    barrierGateIdentificationEntity: BarrierGateIdentificationEntity;
}): JSX.Element {
    switch (barrierGateIdentificationEntity.identificationMedia) {
        case IdentificationType.BADGE:
            return (
                <IdentificationIcon
                    identificationType={IdentificationType.BADGE}
                    licensePlateType={null}
                />
            );
        case IdentificationType.LICENSE_PLATE:
            return (
                <IdentificationIcon
                    identificationType={IdentificationType.LICENSE_PLATE}
                    licensePlateType={
                        barrierGateIdentificationEntity.licenseGroup
                    }
                />
            );
        case IdentificationType.QR_CODE:
            return (
                <IdentificationIcon
                    identificationType={IdentificationType.QR_CODE}
                    licensePlateType={null}
                />
            );
        case IdentificationType.TICKET:
            return (
                <IdentificationIcon
                    identificationType={IdentificationType.TICKET}
                    licensePlateType={null}
                />
            );
    }
}

function BarrierGateIdentificationEnityText({
    barrierGateIdentificationEntity,
    barrierGateProvider,
}: {
    barrierGateIdentificationEntity: BarrierGateIdentificationEntity;
    barrierGateProvider?: TerminalProvider | null;
}) {
    switch (barrierGateIdentificationEntity.identificationMedia) {
        case IdentificationType.BADGE:
            return (
                <Box sx={{ paddingLeft: '5px' }}>
                    {addSpacesEveryFourthFromEnd(
                        barrierGateIdentificationEntity.labelNr,
                    )}
                    {barrierGateIdentificationEntity.rfidDecimal && (
                        <DecimalProviderLabel
                            barrierGateProvider={barrierGateProvider}
                        />
                    )}
                </Box>
            );
        case IdentificationType.LICENSE_PLATE:
            return (
                <Box sx={{ paddingLeft: '5px' }}>
                    {barrierGateIdentificationEntity.countryCode
                        ? `${barrierGateIdentificationEntity.countryCode} - `
                        : ''}
                    {barrierGateIdentificationEntity.licenseNumber}
                </Box>
            );
        case IdentificationType.QR_CODE:
            return (
                <Box sx={{ paddingLeft: '5px' }}>
                    {barrierGateIdentificationEntity.qrCodeId}
                </Box>
            );
        case IdentificationType.TICKET:
            return (
                <Box sx={{ paddingLeft: '5px' }}>
                    {barrierGateIdentificationEntity.ticket.ticketDisplay}
                </Box>
            );
    }
}

function DecimalProviderLabel({
    barrierGateProvider,
}: {
    barrierGateProvider?: TerminalProvider | null;
}): JSX.Element | null {
    if (!barrierGateProvider) {
        return null;
    }
    switch (barrierGateProvider) {
        case TerminalProvider.SKIDATA_CLOUD:
        case TerminalProvider.SKIDATA_CLOUD_V7:
            return (
                <Localized
                    de="Skidata-Nr. "
                    fr="N° Skidata "
                    it="Nr. Skidata "
                    en="Skidata Nr "
                />
            );
        case TerminalProvider.SB:
            return (
                <Localized
                    de="Scheidt & Bachmann-Nr. "
                    fr="N° Scheidt & Bachmann "
                    it="Nr. Scheidt & Bachmann"
                    en="Scheidt & Bachmann Nr "
                />
            );
        case TerminalProvider.DESIGNA:
        case TerminalProvider.HUB_PARKING:
        case TerminalProvider.PM:
        case TerminalProvider.Sitax:
        case TerminalProvider.VB:
        case TerminalProvider.CLOUD_CONNECTOR:
            return null;
    }
}

export function getDurationFormatted(
    start: string,
    end: string,
    language: string,
) {
    const durationTexts = Date.Formatter.durationTexts[language];
    return getDuration(start, end, durationTexts);
}

export function getNetPayedPriceString(
    price: number,
    refundAmount: number | null,
) {
    const amount = refundAmount ? price - refundAmount : price;
    return refundAmount != null
        ? `CHF ${amount.toFixed(2)} (R)`
        : `CHF ${amount.toFixed(2)}`;
}

export function ApprovingContractInfoField({
    approvingContractInfo,
    poolFullInfo,
    bProfile,
}: {
    approvingContractInfo: ApprovingContractInfo | null;
    poolFullInfo?: PoolFullInfo | null;
    bProfile?: number | null;
}) {
    const permitLabel = (
        <Localized
            de="Bewilligung"
            fr="Autorisation"
            it="Autorizzazione"
            en="Permit"
        />
    );
    if (poolFullInfo) {
        return (
            <Box>
                <InputLabel sx={{ lineHeight: '1em' }}>
                    {permitLabel}
                </InputLabel>
                <PoolFullInfoBox
                    poolFullContractExternalId={
                        poolFullInfo.poolFullContractExternalId
                    }
                    poolFullPermitTypeName={poolFullInfo.poolFullPermitTypeName}
                />
            </Box>
        );
    }
    if (approvingContractInfo) {
        switch (approvingContractInfo.approvingContractType) {
            case ApprovingContractType.PRODUCT:
                return (
                    <ReadOnlyTextField
                        label={
                            <Localized
                                de="Produkt"
                                fr="Produit"
                                it="Prodotto"
                                en="Product"
                            />
                        }
                        value={
                            approvingContractInfo.linkApprovingContract ? (
                                <Link
                                    to={`../../${
                                        approvingContractInfo.isGuestProduct
                                            ? 'guest-'
                                            : 'parkingabo/'
                                    }products/${
                                        approvingContractInfo.approvingContractExternalId
                                    }/detail?STATUS=ACTIVE`}
                                >
                                    <Localized
                                        {...approvingContractInfo.infoText}
                                    />
                                </Link>
                            ) : (
                                <Localized
                                    {...approvingContractInfo.infoText}
                                />
                            )
                        }
                    />
                );
            case ApprovingContractType.PERMIT:
                return (
                    <ReadOnlyTextField
                        label={permitLabel}
                        value={
                            <Localized {...approvingContractInfo.infoText} />
                        }
                    />
                );
        }
    }
    return (
        <ReadOnlyTextField
            label={permitLabel}
            value={bProfile ? `n/d (profile ${bProfile})` : EmDash}
        />
    );
}

function PoolFullInfoBox({
    poolFullContractExternalId,
    poolFullPermitTypeName,
}: {
    poolFullContractExternalId: string;
    poolFullPermitTypeName: string;
}) {
    return (
        <>
            <Box sx={{ color: Colors.red, fontWeight: '500' }}>
                <Localized
                    de="Nicht gültig bei der Einfahrt"
                    fr="Non valable à l'entrée"
                    it="Non valido all'ingresso"
                    en="Not valid at the entrance"
                />
                <br />
                <Box sx={{ fontSize: '14px' }}>
                    <Localized
                        de="(max. Anzahl anwesende Fahrzeuge erreicht)"
                        fr="(nombre max de véhicules présents atteint)"
                        it="(raggiunto il numero massimo di veicoli presenti)"
                        en="(max. number of vehicles present reached)"
                    />
                </Box>
            </Box>
            <Box
                sx={{
                    color: theme => theme.palette.blue.main,
                    fontWeight: '500',
                    fontSize: '16px',
                    paddingBottom: '5px',
                }}
            >
                {`${poolFullPermitTypeName} (${poolFullContractExternalId})`}
            </Box>
        </>
    );
}

export function OffstreetTransactionTransit({
    transitsInfo,
}: {
    transitsInfo: PassageInfo[] | null;
}) {
    if (transitsInfo == null) {
        return null;
    }

    return (
        <ReadOnlyTextField
            label={
                <Localized
                    de="Durchfahrt"
                    fr="Transit"
                    it="Transito"
                    en="Transit"
                />
            }
            value={
                transitsInfo.length === 0
                    ? EmDash
                    : transitsInfo.map(transitInfo => (
                          <OffstreetTransactionPassageText
                              key={transitInfo.time}
                              passageInfo={transitInfo}
                          />
                      ))
            }
        />
    );
}

export function OffstreetTransactionPassageText({
    passageInfo,
}: {
    passageInfo: PassageInfo;
}) {
    const startFormatted = Formatter.dayMonthYearHourMinute(
        DateTime.fromISO(passageInfo.time),
    );
    return (
        <Stack
            direction={'row'}
            spacing={1}
            sx={{
                width: '464px',
            }}
        >
            <Box
                sx={{
                    whiteSpace: 'nowrap',
                }}
            >
                {startFormatted}
            </Box>
            {passageInfo.annotation && (
                <Box
                    sx={{
                        color: '#A8A8A8',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                    }}
                >
                    {passageInfo.annotation}
                </Box>
            )}
        </Stack>
    );
}

export function OffstreetTransactionActionText({
    action,
}: {
    action: OffstreetTransactionActions;
}): JSX.Element {
    switch (action) {
        case OffstreetTransactionActions.RECODE:
            return (
                <Localized
                    de="Rabatt erfassen"
                    fr="Saisir rabais"
                    it="Registra ribasso"
                    en="Record discount"
                />
            );
        case OffstreetTransactionActions.REIMBURSE_CUSTOMER:
            return (
                <Localized
                    de="Zurückzahlen"
                    fr="Rembourser"
                    it="Rimborsa"
                    en="Refund"
                />
            );
        case OffstreetTransactionActions.DOWNLOAD_RECEIPT:
            return (
                <Localized
                    de="Quittung herunterladen"
                    fr="Télécharger le reçu"
                    it="Scarica ricevuta"
                    en="Download receipt"
                />
            );
        case OffstreetTransactionActions.CANCEL_ENTRY:
            return (
                <Localized
                    de="Einfahrt annullieren"
                    fr="Supprimer l'entrée"
                    it="Cancella entrata"
                    en="Delete entry"
                />
            );
        case OffstreetTransactionActions.DISABLE_ANTIPASSBACK:
            return (
                <Localized
                    de="Antipassback deaktivieren"
                    fr="Désactiver antipassback"
                    it="Disattiva antipassback"
                    en="Deactivate antipassback"
                />
            );
        case OffstreetTransactionActions.GENERATE_GUEST_PERMIT:
            return (
                <Localized
                    de="Gastkarte generieren"
                    fr="Générer carte d'hôte"
                    it="Crea carta ospite"
                    en="Generate guest card"
                />
            );
    }
}

export function OffstreetTransactionRefundBlocked({
    refundBlockedExplanation,
}: {
    refundBlockedExplanation: TransactionRefundBlockedExplanation;
}): JSX.Element {
    switch (refundBlockedExplanation) {
        case TransactionRefundBlockedExplanation.ALREADY_REFUNDED:
            return (
                <Localized
                    de="Diese Transaktion wurde bereits zurückerstattet"
                    fr="Cette transaction a déjà été remboursée"
                    it="Questa transazione è già stata rimborsata"
                    en="This transaction has already been refunded"
                />
            );
        case TransactionRefundBlockedExplanation.PAST_REFUND_TIME:
            return (
                <Localized
                    de="Rückerstattung nicht mehr möglich: Transaktion älter als 2 Monate"
                    fr="Remboursement n'est plus possible: transaction de plus de 2 mois"
                    it="Rimborso non più possibile: transazione più vecchia di 2 mesi"
                    en="Refund no longer possible: transaction older than 2 months"
                />
            );
        case TransactionRefundBlockedExplanation.MISSING_PERMISSIONS:
            return (
                <Localized
                    de="Funktion nicht verfügbar: Ihnen fehlen die erforderlichen Berechtigungen"
                    fr="Fonction non disponible: vous ne disposez pas des droits nécessaires"
                    it="Funzione non disponibile: non dispone dei diritti necessari"
                    en="Function not available: you don't have the necessary permissions"
                />
            );
        case TransactionRefundBlockedExplanation.NO_DIGITAL_PURCHASE:
            return (
                <Localized
                    de="Rückerstattung nicht möglich: es gibt keine Online-Zahlung"
                    fr="Remboursement impossible: il n'y a aucun paiement en ligne"
                    it="Rimborso non possibile: non esiste alcun pagamento online"
                    en="Refund not possible: there is no online payment"
                />
            );
        case TransactionRefundBlockedExplanation.NON_POSITIVE_PRICE:
            return (
                <Localized
                    de="Rückerstattung nicht möglich: Gratis Transaktion"
                    fr="Remboursement impossible: transaction gratuite"
                    it="Rimborso non possibile: transazione gratuita"
                    en="Refund not possible: free transaction"
                />
            );
        case TransactionRefundBlockedExplanation.PARKING_NOT_COMPLETE:
            return (
                <Localized
                    de="Rückerstattung nicht möglich: die Transaktion ist noch nicht beendet"
                    fr="Remboursement impossible: la transaction n'est pas encore terminé"
                    it="Rimborso non possibile: la transazione non è ancora terminata"
                    en="Refund not possible: the transaction is not yet complete"
                />
            );
        case TransactionRefundBlockedExplanation.NO_ACTIVE_ACCOUNT_TO_REFUND:
            return (
                <Localized
                    de="Rückerstattung nicht möglich: Benutzerkonto gesperrt"
                    fr="Remboursement impossible: compte utilisateur bloqué"
                    it="Rimborso non possibile: conto utente bloccato"
                    en="Refund not possible: user account blocked"
                />
            );
        case TransactionRefundBlockedExplanation.CONTRACT_TYPE_NOT_SUPPORTED:
            return (
                <Localized
                    de="Funktion nicht verfügbar für diese Transaktionstyp"
                    fr="Fonction non disponible pour ce type de transaction"
                    it="Funzione non disponibile per questo tipo di transazione"
                    en="Function not available for this transaction type"
                />
            );
        case TransactionRefundBlockedExplanation.NO_SUBSCRIPTION_CHARGE:
            return (
                <Localized
                    de="Es gibt noch keine Belastungen. Daher ist keine Rückerstattung möglich."
                    fr="Aucun débit n'a encore été effectué. Aucun remboursement n'est donc possible."
                    it="Non sono stati ancora effettuati addebiti. Pertanto nessun rimborso è possibile."
                    en="There have not been any charges yet. Therefore no refund is possible."
                />
            );
        case TransactionRefundBlockedExplanation.PAYMENT_CHANNEL_NOT_SUPPORTED:
            return (
                <Localized
                    de="Rückerstattung nicht möglich: Zahlungsmethode wird nicht unterstützt"
                    fr="Remboursement impossible: méthode de paiement non prise en charge"
                    it="Rimborso non possibile: metodo di pagamento non supportato"
                    en="Refund not possible: payment method not supported"
                />
            );
    }
}

export function NoReceiptForRunningContract() {
    return (
        <Localized
            de="Quittung nicht verfügbar: die Transaktion ist noch nicht beendet"
            fr="Reçu pas disponible: la transaction n'est pas encore terminé"
            it="Ricevuta non disponibile: la transazione non è ancora terminata"
            en="Receipt not available: the transaction is not yet complete"
        />
    );
}

export function NoReceiptForContractWithoutPayments() {
    return (
        <Localized
            de="Quittung nicht verfügbar: es gibt keine Zahlungen"
            fr="Reçu pas disponible : il n'y a aucun paiement"
            it="Ricevuta non disponibile: non ci sono pagamenti"
            en="Receipt not available: there are no payments"
        />
    );
}
